import { z } from 'zod'
import { FadeCurve, GainDb, Id, Milliseconds, Scope, VariableValue, Vec2 } from './primitives.js'

/**
 * Every action can be delayed. Timing chains — the scream, then footsteps three
 * seconds later — are expressed with offsets inside one rule rather than with
 * a chain of timer rules that nobody can read afterwards.
 */
const actionBase = {
  delayMs: Milliseconds.default(0),
}

const PlayAction = z.object({
  type: z.literal('play'),
  ...actionBase,
  emitterId: Id,
  fromOffsetMs: Milliseconds.default(0),
})

const StopAction = z.object({
  type: z.literal('stop'),
  ...actionBase,
  emitterId: Id,
  fadeMs: Milliseconds.default(0),
})

const SetPlaybackAction = z.object({
  type: z.literal('setPlayback'),
  ...actionBase,
  emitterId: Id,
  state: z.enum(['pause', 'resume']),
})

/** Fades either an emitter or a whole bus; bus fades are how ducking scenes are driven. */
const FadeToAction = z.object({
  type: z.literal('fadeTo'),
  ...actionBase,
  target: z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('emitter'), emitterId: Id }),
    z.object({ kind: z.literal('bus'), busId: Id }),
  ]),
  toGainDb: GainDb,
  overMs: Milliseconds.default(500),
  curve: FadeCurve.default('equalPower'),
})

const CrossfadeAction = z.object({
  type: z.literal('crossfade'),
  ...actionBase,
  fromEmitterId: Id,
  toEmitterId: Id,
  overMs: Milliseconds.default(1000),
  curve: FadeCurve.default('equalPower'),
})

/**
 * A transient sound that needs no authored emitter — the operator dropping a
 * whisper two metres behind a specific guest, or a twig snapping somewhere in
 * the woods. It cleans itself up when it finishes.
 */
const SpawnOneShotAction = z.object({
  type: z.literal('spawnOneShot'),
  ...actionBase,
  assetId: Id,
  at: z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('point'), position: Vec2 }),
    z.object({
      kind: z.literal('listenerRelative'),
      forwardM: z.number().finite().default(0),
      rightM: z.number().finite().default(0),
      upM: z.number().finite().default(0),
    }),
  ]),
  busId: Id,
  gainDb: GainDb.default(0),
})

const MoveEmitterAction = z.object({
  type: z.literal('moveEmitter'),
  ...actionBase,
  emitterId: Id,
  to: Vec2,
  /** Zero teleports; anything else glides, which is what makes movement audible. */
  overMs: Milliseconds.default(0),
})

const PathControlAction = z.object({
  type: z.literal('pathControl'),
  ...actionBase,
  emitterId: Id,
  command: z.enum(['start', 'stop', 'restart']),
  speedMps: z.number().finite().positive().optional(),
})

const SetVariableAction = z.object({
  type: z.literal('setVariable'),
  ...actionBase,
  name: z.string().max(64),
  scope: Scope.default('listener'),
  assign: z.discriminatedUnion('op', [
    z.object({ op: z.literal('set'), value: VariableValue }),
    z.object({ op: z.literal('add'), value: z.number().finite() }),
  ]),
})

const SetRuleEnabledAction = z.object({
  type: z.literal('setRuleEnabled'),
  ...actionBase,
  ruleIds: z.array(Id).min(1),
  enabled: z.boolean(),
})

/** Text in the player. Doubles as the accessibility surface for audio-only content. */
const ShowCardAction = z.object({
  type: z.literal('showCard'),
  ...actionBase,
  title: z.string().max(120),
  body: z.string().max(2000).default(''),
  imageAssetId: Id.optional(),
  durationMs: Milliseconds.default(8000),
})

export const Action = z.discriminatedUnion('type', [
  PlayAction,
  StopAction,
  SetPlaybackAction,
  FadeToAction,
  CrossfadeAction,
  SpawnOneShotAction,
  MoveEmitterAction,
  PathControlAction,
  SetVariableAction,
  SetRuleEnabledAction,
  ShowCardAction,
])
export type Action = z.infer<typeof Action>
export type ActionType = Action['type']
